import type { UUID } from '@/types/Id'
import type { RelevancyThreshold } from '@/types/Schedule'

interface NewOpportunitiesQueryKeyArgs {
  orgId: UUID | ''
}

export type OpportunityType =
  | 'federal_contract'
  | 'federal_grant'
  | 'sbir'
  | 'sled_contract'
  | 'dibbs_contract'

interface SearchTag {
  title: string
  value: string
}

interface BaseOpportunityResult {
  id: UUID
  type: OpportunityType
  title: string
  description: string
  active: boolean
  posted_date?: string
  active_until?: string
  opportunity_id?: string
  pursuit_id?: UUID
  search_id?: UUID
  tags: SearchTag[]
  relevance_tier?: RelevancyThreshold
}

interface SamGovResult extends BaseOpportunityResult {
  type: 'federal_contract'
  samgov_opportunity: {
    department?: string
    sub_tier?: string
    office?: string
    notice_type?: string
    set_aside_code?: string
    set_aside_description?: string
    naics_code?: string
    naics_industry?: string
    psc_code?: string
    psc_industry?: string
    place_of_performance_state?: string
    place_of_performance_city?: string
    ai_summary: string
  }
}

interface GrantsGovResult extends BaseOpportunityResult {
  type: 'federal_grant'
  grantsgov_opportunity: {
    agency_name?: string
    status?: string
    opportunity_number?: string
    funding_instrument_type?: string
    category_of_funding_activity?: string
    award_ceiling?: number
    award_floor?: number
    estimated_total_program_funding?: number
    expected_number_of_awards?: number
    close_date?: string
    ai_summary: string
  }
}

interface SbirResult extends BaseOpportunityResult {
  type: 'sbir'
  sbir_opportunity: {
    program?: string
    phase?: string
    agency?: string
    branch?: string
    status?: string
    solicitation_number?: string
    application_due_date?: string
    ai_summary: string
  }
}

interface StateLocalResult extends BaseOpportunityResult {
  type: 'sled_contract'
  statelocal_opportunity: {
    category?: string
    location?: string
    buyer_name?: string
    status?: string
    due_date?: string
    type?: string
    jurisdiction_names?: string[]
    jurisdiction_prefixes?: string[]
    ai_summary: string
  }
}

interface DibbsResult extends BaseOpportunityResult {
  type: 'dibbs_contract'
  dibbs_opportunity: {
    nsn_number?: string
    quantity?: string
    unit_of_measure?: string
    unit_price?: string
    has_technical_documents?: boolean
    cage_codes?: string[]
    part_numbers?: string[]
    set_aside_code?: string
    ai_summary: string
  }
}

export type OpportunitySearchResult =
  | SamGovResult
  | GrantsGovResult
  | SbirResult
  | StateLocalResult
  | DibbsResult

export interface RecommendedOpportunitiesList {
  data: OpportunitySearchResult[]
}

export const newOpportunitiesQueryKeys = {
  all: () => ['new-opportunities'] as const,
  organization: (keyArgs: NewOpportunitiesQueryKeyArgs) =>
    ['new-opportunities', keyArgs.orgId] as const,
}
